import { useEffect, useRef } from "react";
import { AppointmentTime } from "../../../types";

interface UseTimeSlotFocusOptions {
  availableAppointmentTimes: AppointmentTime[];
  loadingAvailableTimes: boolean;
  selectedDate: Date;
}

/**
 * Custom hook to manage focus on time slots after date selection.
 * When a date is selected and times load, automatically focus the first available time slot.
 */
export const useTimeSlotFocus = ({
  availableAppointmentTimes,
  loadingAvailableTimes,
  selectedDate
}: UseTimeSlotFocusOptions): void => {
  const previousSelectedDateRef = useRef<Date | null>(null);
  const wasLoadingRef = useRef<boolean>(false);

  useEffect(() => {
    // Track if we were just loading (transition from loading to not loading)
    const justFinishedLoading = wasLoadingRef.current && !loadingAvailableTimes;
    wasLoadingRef.current = loadingAvailableTimes;

    // Check if the date changed (not just the initial render)
    const dateChanged =
      previousSelectedDateRef.current !== null &&
      previousSelectedDateRef.current.toDateString() !== selectedDate.toDateString();

    previousSelectedDateRef.current = selectedDate;

    // Only focus if:
    // 1. We just finished loading (not loading anymore but were loading before)
    // 2. The date changed (user selected a new date)
    // 3. There are available times to focus on
    if (justFinishedLoading && dateChanged && availableAppointmentTimes.length > 0) {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        setTimeout(() => {
          // Find the first time slot button
          // The buttons are in AppointmentTimeList component with specific styling classes
          // Look for buttons with the turquoise background that indicates available time slots
          const firstTimeButton = document.querySelector<HTMLButtonElement>(
            "button.bg-turquoise-200.hover\\:bg-turquoise-600"
          );

          if (firstTimeButton) {
            firstTimeButton.focus();
          }
        }, 150); // Small delay to ensure the spinner is hidden and buttons are rendered
      });
    }
  }, [availableAppointmentTimes, loadingAvailableTimes, selectedDate]);
};
